<template>
  <UForm
    v-if="schema && state"
    :schema="schema"
    :state="state"
    class="w-full"
    @submit="onSubmit"
  >
    <div class="grid grid-cols-12 gap-y-1 lg:gap-y-0 lg:gap-x-1">
      <div class="col-span-12 lg:col-span-5">
        <UInputMenu
          v-model="state.query"
          v-model:search-term="searchTerm"
          :items="items"
          :loading="loading"
          :ui="{
            itemLabel: 'hidden',
            item: 'data-highlighted:not-data-disabled:before:bg-primary-100 data-highlighted:not-data-disabled:before:ring-2 data-highlighted:not-data-disabled:before:bg-primary-100 data-highlighted:not-data-disabled:before:ring-primary-500 ring-inset',
          }"
          ignore-filter
          :label-key="'label'"
          icon="i-heroicons-magnifying-glass-20-solid"
          :placeholder="t('components.search.query')"
          :aria-label="t('components.search.query')"
          size="xl"
          :content="{
            avoidCollisions: false,
          }"
          autocomplete="on"
        >
          <template #trailing>
            <span class="sr-only">{{ t('a11y.options') }}</span>
          </template>
          <template #item="{ item }">
            <div v-if="item" class="flex">
              <div>
                <UIcon
                  :name="
                    item.rawType === 'COTTAGE'
                      ? 'i-heroicons-home'
                      : 'i-heroicons-map-pin'
                  "
                  class="h-5 w-5 text-escapadaGray-400"
                  aria-hidden="true"
                />
              </div>
              <div class="flex flex-col ml-2">
                <TText size="sm" classes="mb-1">
                  {{ item.label }}
                </TText>
                <TText size="xs" color="text-gray-600" classes="mb-1">
                  ({{ item.description }})
                </TText>
              </div>
            </div>
          </template>
          <template #empty> {{ t('components.search.noResults') }} </template>
        </UInputMenu>
      </div>

      <div class="col-span-12 lg:col-span-3">
        <TDateInput v-model:dates="dateModel" clear-dates />
      </div>

      <div class="col-span-12 lg:col-span-2">
        <UInput
          v-model="state.capacity"
          type="number"
          min="0"
          class="grow"
          icon="i-heroicons-users-20-solid"
          :placeholder="t('components.search.capacity')"
          :aria-label="t('components.search.capacity')"
          size="xl"
          autocomplete="on"
        />
      </div>

      <div class="col-span-12 lg:col-span-2">
        <UButton color="primary" variant="solid" block type="submit" size="xl">
          {{ t('components.search.submit') }}
        </UButton>
      </div>
    </div>
  </UForm>
</template>

<script setup lang="ts">
import { z } from 'zod'
import type { DatePickerRangeObject } from '@angelblanco/v-calendar/dist/types/src/use/datePicker.js'
import fetchSearchSuggest from '~~/modules/api/rurality/v3/search/suggest'

interface propsInterface {
  location?: string
}
const props = defineProps<propsInterface>()

const loading = ref(false)
const searchTerm = ref<string>('')

type CottageSuggestion = {
  type: 'item'
  rawType: 'COTTAGE'
  name: string
  denomination?: string
  parentName?: string
  description?: string
  url: string
  location: null
  label: string
}
type LocationSuggestion = {
  type: 'item'
  rawType: 'village' | 'province' | 'region' | 'country' | 'zone'
  name: string
  denomination?: string
  parentName?: string
  description?: string
  url: null
  location: string
  label: string
}
type Suggestion = CottageSuggestion | LocationSuggestion

const items = ref<Suggestion[]>([])

// FORM
const schema = z.object({
  query: z
    .discriminatedUnion('rawType', [
      z.object({
        type: z.literal('item'),
        rawType: z.literal('COTTAGE'),
        name: z.string(),
        denomination: z.string().optional(),
        parentName: z.string().optional(),
        description: z.string().optional(),
        url: z.string(),
        location: z.null(),
        label: z.string(),
      }),
      z.object({
        type: z.literal('item'),
        rawType: z.enum(['village', 'province', 'region', 'country', 'zone']),
        name: z.string(),
        denomination: z.string().optional(),
        parentName: z.string().optional(),
        description: z.string().optional(),
        url: z.null(),
        location: z.string(),
        label: z.string(),
      }),
    ])
    .optional(),
  dates: z.custom<DatePickerRangeObject>().optional().nullable(),
  capacity: z
    .number()
    .optional()
    .or(z.literal(''))
    .transform(value => (value === '' ? undefined : value)),
})

interface State {
  query?: Suggestion
  dates?: DatePickerRangeObject | null
  capacity?: number
}

const state: State = reactive({
  query: undefined,
  dates: null,
  capacity: undefined,
})

const dateModel = computed<DatePickerRangeObject | null>({
  get: () => state.dates ?? null,
  set: v => {
    state.dates = v
  },
})

watch(
  () => searchTerm.value,
  async searchTerm => {
    items.value = await search(searchTerm)
  },
)

function mapSuggestionToSuggestion(suggestion: any): Suggestion {
  // Transform null to undefined to match component types
  const transformNullToUndefined = (value: string | null | undefined) =>
    value === null ? undefined : value

  if (suggestion.type === 'COTTAGE') {
    const result: CottageSuggestion = {
      ...suggestion,
      type: 'item',
      rawType: 'COTTAGE',
      label: suggestion.name,
      location: null,
      denomination: transformNullToUndefined(suggestion.denomination),
      parentName: transformNullToUndefined(suggestion.parentName),
      description: transformNullToUndefined(suggestion.description),
    }
    return result
  } else {
    const result: LocationSuggestion = {
      ...suggestion,
      type: 'item',
      rawType: suggestion.type,
      label: suggestion.name,
      url: null,
      denomination: transformNullToUndefined(suggestion.denomination),
      parentName: transformNullToUndefined(suggestion.parentName),
      description: transformNullToUndefined(suggestion.description),
    }
    return result
  }
}

async function search(q: string) {
  loading.value = true

  const data = await fetchSearchSuggest({
    query: q,
    province: props.location,
  })

  loading.value = false

  if (data.success) {
    return data.success.data.suggestions.map(mapSuggestionToSuggestion)
  }

  return []
}

async function onSubmit() {
  if (import.meta.client) {
    let datesString
    if (state.dates && state.dates.start && state.dates.end) {
      const startString = toRuralityDate().legacyDate(state.dates.start as Date)
      const endString = toRuralityDate().legacyDate(state.dates.end as Date)
      datesString = `${startString},${endString}`
    }

    if (!state.query) {
      navigateTo(
        routes().cottageList({
          l: props.location,
          d: datesString,
          c: state.capacity ? state.capacity.toString() : '',
        }),
        {
          external: true,
        },
      )
    } else {
      if (state.query.rawType === 'COTTAGE') {
        navigateTo(
          routes().filteredCottage({
            url: state.query.url,
            dateIn:
              state.dates && state.dates.start
                ? toRuralityDate().legacyDate(state.dates.start as Date)
                : undefined,
            dateOut:
              state.dates && state.dates.end
                ? toRuralityDate().legacyDate(state.dates.end as Date)
                : undefined,
            capacity: state.capacity?.toString(),
          }),
          {
            external: true,
          },
        )
      } else {
        navigateTo(
          routes().cottageList({
            d: datesString,
            c: state.capacity?.toString(),
            l: state.query.location,
          }),
          {
            external: true,
          },
        )
      }
    }
  }
}
</script>
